import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { PageLayout } from '@/components/Layout/PageLayout';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import { Textarea } from '@/components/Common/textarea';
import ToggleButtonGroup from '@/components/Common/ToggleButtonGroup';
import { DomainSelector } from '@/components/Common/DomainSelector';
import { getUseCaseStatusLabel, USE_CASE_STATUS_VALUES } from '@/components/UseCases/useCaseStatus';
import { Domain, UseCaseCreate, UseCaseStatus } from '@/types';
import { useCaseService } from '@/services/useCaseService';
import { USER_JOURNEY_TO_CADRAGE_STEP } from '@/config/onboardingTours';
import { useTourStore } from '../../stores/tourStore';

const SHORT_DESCRIPTION_MAX_LENGTH = 200;

const PREDEFINED_TOOLS = [
  'SAP',
  'Salesforce',
  'Power BI',
  'Tableau',
  'Excel',
  'Python',
  'SQL',
  'Azure',
  'AWS',
];

const NewUseCasePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    description: '',
    business_need: '',
    input: '',
    output: '',
    technical_elements: '',
    involved_tools: [] as string[],
    status: 'Identified' as UseCaseStatus,
    cadrage_id: undefined as string | undefined,
  });

  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);

  useEffect(() => {
    const preselectedDomain = (location.state as { domain?: Domain } | null)?.domain;
    if (preselectedDomain) {
      setSelectedDomain(preselectedDomain);
    }
  }, [location.state]);

  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Tools typed by the user stay listed (as checkboxes) even once unticked.
  const [customTools, setCustomTools] = useState<string[]>([]);
  const [customTool, setCustomTool] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Progressive enrichment: only required fields are shown by default; users
  // can opt-in to fill the optional sections at creation time.
  const [showAdvanced, setShowAdvanced] = useState(false);

  const toolOptions = [...PREDEFINED_TOOLS, ...customTools];

  const statusOptions = USE_CASE_STATUS_VALUES.map((status) => ({
    value: status,
    label: getUseCaseStatusLabel(status, t),
  }));

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('newUseCase.nameErrorRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('newUseCase.nameErrorMinLength');
    }

    if (!selectedDomain) {
      newErrors.domain = t('newUseCase.domainError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload: UseCaseCreate = {
        name: formData.name.trim(),
        domain_id: selectedDomain!.id,
        short_description: formData.short_description?.trim() || null,
        description: formData.description?.trim() || null,
        business_need: formData.business_need?.trim() || null,
        input: formData.input?.trim() || null,
        output: formData.output?.trim() || null,
        technical_elements: formData.technical_elements?.trim() || null,
        involved_tools: selectedTools.length > 0 ? selectedTools : null,
        status: formData.status,
        cadrage_id: formData.cadrage_id || null,
      };

      const newUseCase = await useCaseService.create(payload);

      // If user is in the "Complete User Journey" tour, advance to the cadrage transition step before
      // navigating so the tour continues (guard will auto-go to cadrage creation)
      const { activeTourId, setStep } = useTourStore.getState();
      if (activeTourId === 'userJourney') {
        setStep(USER_JOURNEY_TO_CADRAGE_STEP);
      }

      // Navigate to the new use case detail page
      navigate(`/cas-usage/${newUseCase.id}`);
    } catch (err) {
      console.error('Failed to create use case:', err);
      const errorMessage = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ||
        (err as Error)?.message ||
        t('newUseCase.createError');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/cas-usage');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDomainChange = (domain: Domain | null) => {
    setSelectedDomain(domain);
    if (errors.domain) {
      setErrors(prev => ({
        ...prev,
        domain: '',
      }));
    }
  };

  const handleToggleTool = (tool: string, checked: boolean) => {
    setSelectedTools(prev =>
      checked ? (prev.includes(tool) ? prev : [...prev, tool]) : prev.filter(item => item !== tool)
    );
  };

  const handleAddCustomTool = () => {
    const typed = customTool.trim();
    if (!typed) return;
    // Reuse the listed spelling ("sap" ticks "SAP") so the same tool is never stored twice.
    const existing = toolOptions.find(option => option.toLowerCase() === typed.toLowerCase());
    const tool = existing ?? typed;
    if (!existing) {
      setCustomTools(prev => [...prev, tool]);
    }
    handleToggleTool(tool, true);
    setCustomTool('');
  };

  return (
    <PageLayout
      icon={<Sparkles className="h-6 w-6" />}
      title={t('newUseCase.title')}
      subtitle={t('newUseCase.subtitle')}
    >
      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Form */}
      <div>
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Section: Informations générales */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {t('newUseCase.generalInfo')}
              </h2>

              {/* Name - Required */}
              <div className="space-y-2" data-tour="usecase-name-field">
                <Label htmlFor="name">
                  {t('newUseCase.nameLabel')} <span className="text-destructive">{t('newUseCase.nameRequired')}</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('newUseCase.namePlaceholder')}
                  className={errors.name ? 'border-destructive' : ''}
                  disabled={loading}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.nameHelp')}
                </p>
              </div>

              {/* Domain Selector - Required */}
              <div className="space-y-2" data-tour="usecase-domain-selector">
                <Label htmlFor="domain">
                  {t('global.domain')} <span className="text-destructive">{t('newUseCase.domainRequired')}</span>
                </Label>
                <div className="w-full">
                  <DomainSelector
                    selectedDomainId={selectedDomain?.id || null}
                    onDomainChange={handleDomainChange}
                    className="w-full"
                  />
                </div>
                {errors.domain && (
                  <p className="text-sm text-destructive">{errors.domain}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.domainHelp')}
                </p>
              </div>

              {/* Toggle: progressive enrichment */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {showAdvanced ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {showAdvanced
                    ? t('newUseCase.hideOptional', 'Masquer les détails optionnels')
                    : t('newUseCase.showOptional', 'Ajouter plus de détails (optionnel)')}
                </button>
                {!showAdvanced && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t(
                      'newUseCase.progressiveHint',
                      'Vous pourrez enrichir la fiche après la création — un indicateur de complétude vous y invitera.'
                    )}
                  </p>
                )}
              </div>
            </div>

            {showAdvanced && (
              <>
            <div className="space-y-4">
              {/* Short Description */}
              <div className="space-y-2">
                <Label htmlFor="short_description">{t('newUseCase.shortDescriptionLabel')}</Label>
                {/* Plain text on purpose: it is a one-line summary shown in tables, not a markdown document. */}
                <Textarea
                  id="short_description"
                  markdown={false}
                  value={formData.short_description || ''}
                  onChange={(e) => handleInputChange('short_description', e.target.value)}
                  placeholder={t('newUseCase.shortDescriptionPlaceholder')}
                  rows={3}
                  maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
                  aria-describedby="short_description-help"
                  className="resize-y"
                  disabled={loading}
                />
                <div id="short_description-help" className="flex justify-between gap-2 text-xs text-muted-foreground">
                  <span>{t('newUseCase.shortDescriptionHelp')}</span>
                  <span aria-hidden="true">
                    {(formData.short_description || '').length}/{SHORT_DESCRIPTION_MAX_LENGTH}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label id="status-label">{t('newUseCase.statusLabel')}</Label>
                <div>
                  <ToggleButtonGroup<UseCaseStatus>
                    aria-labelledby="status-label"
                    aria-describedby="status-help"
                    options={statusOptions}
                    value={formData.status}
                    onChange={(value) => handleInputChange('status', value)}
                    disabled={loading}
                  />
                </div>
                <p id="status-help" className="text-xs text-muted-foreground">
                  {t('newUseCase.statusHelp')}
                </p>
              </div>
            </div>

            {/* Section: Description détaillée */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {t('newUseCase.detailedDescription')}
              </h2>

              {/* Description */}
              <div className="space-y-2" data-tour="usecase-description-field">
                <Label htmlFor="description">{t('newUseCase.descriptionLabel')}</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('newUseCase.descriptionPlaceholder')}
                  rows={5}
                  disabled={loading}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t('newUseCase.descriptionHelp')}</span>
                  <span>{(formData.description || '').length}</span>
                </div>
              </div>

              {/* Business Need */}
              <div className="space-y-2" data-tour="usecase-business-need">
                <Label htmlFor="business_need">{t('newUseCase.businessNeedLabel')}</Label>
                <Textarea
                  id="business_need"
                  value={formData.business_need || ''}
                  onChange={(e) => handleInputChange('business_need', e.target.value)}
                  placeholder={t('newUseCase.businessNeedPlaceholder')}
                  rows={3}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.businessNeedHelp')}
                </p>
              </div>
            </div>

            {/* Section: Données et flux */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {t('newUseCase.dataAndFlow')}
              </h2>

              {/* Input */}
              <div className="space-y-2">
                <Label htmlFor="input">{t('newUseCase.inputLabel')}</Label>
                <Textarea
                  id="input"
                  value={formData.input || ''}
                  onChange={(e) => handleInputChange('input', e.target.value)}
                  placeholder={t('newUseCase.inputPlaceholder')}
                  rows={3}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.inputHelp')}
                </p>
              </div>

              {/* Output */}
              <div className="space-y-2">
                <Label htmlFor="output">{t('newUseCase.outputLabel')}</Label>
                <Textarea
                  id="output"
                  value={formData.output || ''}
                  onChange={(e) => handleInputChange('output', e.target.value)}
                  placeholder={t('newUseCase.outputPlaceholder')}
                  rows={3}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.outputHelp')}
                </p>
              </div>
            </div>

            {/* Section: Aspects techniques */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                {t('newUseCase.technicalAspects')}
              </h2>

              {/* Technical Elements */}
              <div className="space-y-2">
                <Label htmlFor="technical_elements">{t('newUseCase.technicalElementsLabel')}</Label>
                <Textarea
                  id="technical_elements"
                  value={formData.technical_elements || ''}
                  onChange={(e) => handleInputChange('technical_elements', e.target.value)}
                  placeholder={t('newUseCase.technicalElementsPlaceholder')}
                  rows={4}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('newUseCase.technicalElementsHelp')}
                </p>
              </div>

              {/* Involved Tools */}
              <fieldset className="space-y-2" aria-describedby="involved_tools-help">
                <legend className="text-sm font-medium leading-none">
                  {t('newUseCase.involvedToolsLabel')}
                </legend>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-2 sm:grid-cols-3">
                  {toolOptions.map((tool, index) => {
                    const checkboxId = `involved_tool-${index}`;
                    return (
                      <label
                        key={tool}
                        htmlFor={checkboxId}
                        className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-foreground hover:bg-muted"
                      >
                        <input
                          id={checkboxId}
                          type="checkbox"
                          checked={selectedTools.includes(tool)}
                          onChange={(e) => handleToggleTool(tool, e.target.checked)}
                          disabled={loading}
                          className="h-4 w-4 shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                        <span className="break-words">{tool}</span>
                      </label>
                    );
                  })}
                </div>

                {/* Free text for a tool that is not listed; it is added and ticked. */}
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={customTool}
                    onChange={(e) => setCustomTool(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomTool();
                      }
                    }}
                    placeholder={t('newUseCase.customToolPlaceholder')}
                    aria-label={t('newUseCase.customToolLabel')}
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddCustomTool}
                    disabled={loading || !customTool.trim()}
                  >
                    {t('global.add')}
                  </Button>
                </div>

                <p id="involved_tools-help" className="text-xs text-muted-foreground">
                  {t('newUseCase.toolsHelp')}
                </p>
              </fieldset>
            </div>
              </>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                {t('global.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="min-w-[140px]"
                data-tour="usecase-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('global.creating')}
                  </div>
                ) : (
                  t('newUseCase.create')
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Help Section */}
      <div>
        <div className="bg-muted rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">
            {t('newUseCase.aboutTitle')}
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t('newUseCase.aboutDescription')}
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>{t('newUseCase.aboutPoint1')}</li>
              <li>{t('newUseCase.aboutPoint2')}</li>
              <li>{t('newUseCase.aboutPoint3')}</li>
              <li>{t('newUseCase.aboutPoint4')}</li>
              <li>{t('newUseCase.aboutPoint5')}</li>
            </ul>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default NewUseCasePage;
